import React, { useState } from 'react';
import { ResponseContent } from './ResponseContent';
import { Message } from '../types/chat';
// Default test results data structure
const defaultTestResults = {
  results: [] as TestResult[],
};
// how to import test results json file
// import testResults from '../../../test_results/income_option_validation_results_development_29_10_25_11_31.json';

// Add TypeScript types for the JSON data
interface Citation {
  title: string;
  text: string;
  knowledge_base: string;
  agent_source: string;
  confidence: number;
  uri: string;
}

interface MetricsAnalysis {
  compliance_percentage: number;
  metrics_found: number;
  metrics_total: number;
  dual_architecture_score: number;
}

interface TestResult {
  question_id: string;
  question: string;
  chatbot_response: string;
  citations: Citation[];
  metrics_analysis?: MetricsAnalysis;
  html_detected: boolean;
  success: boolean;
  error: string | null;
  timestamp: string;
}

interface TestResultsRendererProps {
  title?: string;
  description?: string;
  data?: TestResult[];
}

// Transform the loaded JSON data to the required structure
const transformData = (results: TestResult[]) =>
  results.map((result) => ({
    questionId: result.question_id,
    question: result.question,
    response: result.chatbot_response,
    citations: result.citations,
    metricsAnalysis: {
      compliancePercentage: result.metrics_analysis?.compliance_percentage || 0,
      metricsFound: result.metrics_analysis?.metrics_found || 0,
      metricsTotal: result.metrics_analysis?.metrics_total || 0,
      dualArchitectureScore:
        result.metrics_analysis?.dual_architecture_score || 0,
    },
    htmlDetected: result.html_detected,
    success: result.success,
    error: result.error,
    timestamp: result.timestamp,
  }));

export const TestResultsRenderer: React.FC<TestResultsRendererProps> = ({
  title = 'Chatbot Response Test Results Renderer',
  description = 'Testing how chatbot responses render in the UI with citations and validation metrics',
  data,
}) => {
  const [selectedResult, setSelectedResult] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(true);

  const defaultData = transformData(
    defaultTestResults.results as unknown as TestResult[],
  );
  const transformedData = data ? transformData(data) : defaultData;

  const convertToMessage = (result: any): Message => {
    return {
      id: result.questionId,
      sender: 'agent',
      content: result.response,
      type: 'text',
      timestamp: new Date(result.timestamp),
      citations:
        result.citations?.map((citation: any, index: number) => ({
          ...citation,
          id: `citation-${index + 1}`,
          number: index + 1,
          title: citation.title,
          text: citation.text,
          uri: citation.uri,
          confidence: citation.confidence,
          knowledge_base: citation.knowledge_base,
          agent_source: citation.agent_source,
        })) || [],
    };
  };

  const displayedResults = showAll
    ? transformedData
    : transformedData.slice(0, 3);

  return (
    <div className="h-full w-full">
      <div className="bg-white rounded-lg shadow-lg p-6 h-full flex flex-col">
        <div className="flex-shrink-0">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{title}</h1>
          <p className="text-gray-600 mb-4">{description}</p>

          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setShowAll(!showAll)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              {showAll ? 'Show First 3' : 'Show All Results'}
            </button>
            <div className="text-sm text-gray-500 self-center">
              Showing {displayedResults.length} of {transformedData.length}{' '}
              results
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="space-y-8 pr-2">
            {displayedResults.map((result, index) => {
              const message = convertToMessage(result);
              const isExpanded = selectedResult === index;

              return (
                <div
                  key={result.questionId}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <div
                    className="bg-gray-50 px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => setSelectedResult(isExpanded ? null : index)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          Question {result.questionId}: {result.question}
                        </h3>
                        <div className="text-sm text-gray-600 mt-1">
                          Success: {result.success ? '✅' : '❌'}
                          {result.error && ' | Error: ❌'}
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {isExpanded ? '▼' : '▶'}
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="p-4 bg-white">
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-900 mb-2">
                          Chatbot Response:
                        </h4>
                        <div className="border border-gray-200 rounded p-4">
                          <ResponseContent message={message} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestResultsRenderer;
